import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ServiceNotAvailableState } from '@lumapps/lumx-states/components/ServiceNotAvailableState';
import { ExpandableTreeSkeletons } from '@lumapps/lumx-trees/components/ExpandableTreeSkeletons';
import { usePrevious } from '@lumapps/lumx/hooks/usePrevious';
import { FlexBox } from '@lumapps/lumx/react';
import { isSectionNode, NavigationMapItem } from '@lumapps/navigation/types';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { ConnectedBlockedRedirectionDialog } from '@lumapps/router/components/BlockedRedirectionDialog';
import {
    GLOBAL,
    TranslatableObject,
    TranslateAndReplaceReplacements,
    translateFromApiV2Format,
    TranslateKey,
    useTranslate,
} from '@lumapps/translations';
import { useFocus } from '@lumapps/utils/hooks/useFocus';

import { NavigationAdminConfiguration, NavigationItemWizardOptionProps } from '../../types';
import { isSameNode, isMoveNodeToSectionAllowed } from '../../utils';
import { EditNodeDialog } from '../EditNodeDialog';
import { EmptyNavigationList, EmptyNavigationListProps } from '../EmptyNavigationList';
import { NavigationItemModalProps } from '../NavigationItemModal/NavigationItemModal';
import { NavigationList, NavigationListProps } from '../NavigationList';
import {
    NavigationMapContextProvider,
    NavigationMapContextProviderProps,
} from '../NavigationMapContextProvider/NavigationMapContextProvider';
import { SetNodeDestinationDialog } from '../SetNodeDestinationDialog';

import './index.scss';

export interface NavigationAdminProps extends Omit<NavigationListProps, 'isInsideSection'> {
    /** callback to be executed for blocking the frontend browser navigation  */
    blockNavigation(): void;
    /** callback to be executed in order to unblock the frontend browser navigation */
    unblockNavigation(): void;
    /** Callback to set the navigation into a loading state */
    setIsLoading(isLoading: boolean): void;
    /** Callback to be executed when the page is on error state and user reloads. */
    onReload?(): void;
    /** string to be displayed for the empty state title */
    emptyTitle?: EmptyNavigationListProps['title'];
    /** string to be displayed for the empty state message */
    emptyMessage?: EmptyNavigationListProps['message'];
    /** Message to be displayed once a node has been moved to the first level */
    movedToFirstLevelKeyMessage: string;
    /** whether the navigation is loading */
    isLoading: boolean;
    /** whether the error state should be displayed */
    hasError?: boolean;
    /** whether the navigation has changed or not */
    isChanged?: boolean;
    /** ID of the current navigation state */
    tempId?: string;
    /** scope to be used for tracking purposes */
    scope: string;
    /**
     * Wizard options to display.
     * * set option as null to hide it
     * * set option as true to display it
     * * set option as false to display it as disabled
     */
    wizardOptions?: NavigationItemWizardOptionProps['options'];
    /** Override default parameters for all NavigationItemModals */
    navigationItemModalProps?: Partial<NavigationItemModalProps>;
    /** Callback when the addNavigationDialog is toggled */
    onToggleDialog?(isDialogOpen: boolean): void;
    /** Whether the addNavigationItem dialog is open or not */
    addItemTargetId?: string;
    /** Dictionary of all groups set as "visible by" */
    visibleByGroupReferences?: NavigationMapContextProviderProps['visibleByGroupReferences'];
    /**
     * Information of the default user feeds.
     * Useful for filtering to detect whether a feed has specific visibility rights.
     */
    defaultFeeds?: NavigationMapContextProviderProps['defaultFeeds'];
    /**
     * The configuration on the navigation admin.
     * Contains the information of what should or should not be displayed
     */
    configuration: NavigationAdminConfiguration;
}

const CLASSNAME = 'navigation-admin';

/**
 * Internal navigation admin that connects all components necessary to manage the navigation
 * @param NavigationAdminProps
 */
export const NavigationAdmin: React.FC<NavigationAdminProps> = ({
    isLoading,
    isChanged,
    nodes,
    id,
    onAddNode,
    onEditNode,
    onDeleteNode,
    blockNavigation,
    unblockNavigation,
    onReplaceNodes,
    setIsLoading,
    onReload,
    hasError,
    currentLanguage,
    languages,
    maxDepthAllowed,
    maxWidthAllowed,
    depthLimitReachedMessage,
    widthLimitReachedMessage,
    moveNodeRestrictionMessage,
    emptyTitle,
    emptyMessage,
    movedToFirstLevelKeyMessage,
    scope,
    wizardOptions,
    navigationItemModalProps,
    onToggleDialog,
    addItemTargetId,
    visibleByGroupReferences,
    defaultFeeds,
    configuration,
    sectionPermission,
    ...rest
}) => {
    const { success } = useNotification();
    const { translateObject } = useTranslate();
    const [moreButtonRef, setMoreButtonRef] = React.useState(null);
    const [moveNodeDialog, setMoveNodeDialog] = useState<{ node?: NavigationMapItem; isOpen: boolean }>({
        isOpen: false,
    });

    const [editNodeDialog, setEditNodeDialog] = useState<{
        node?: NavigationMapItem;
        isOpen: boolean;
        path?: string[];
    }>({
        isOpen: false,
    });

    const [wizardDialogOpen, setWizardDialogOpen] = useState<boolean>(false);

    /**
     * As the move and edit dialogs are not rendered at all when closed,
     * manually reset the focus on the trigger button on close instead of using the "parentElement" prop.
     */
    const previousMoveNodeDialogIsOpen = usePrevious(moveNodeDialog.isOpen);
    useFocus(moreButtonRef, !moveNodeDialog.isOpen && previousMoveNodeDialogIsOpen);
    const previousEditNodeDialogIsOpen = usePrevious(editNodeDialog.isOpen);
    useFocus(moreButtonRef, !editNodeDialog.isOpen && previousEditNodeDialogIsOpen);

    /**
     * Get a list of all sections or node that cans be a listNode at root.
     */
    const rootSectionIds = useMemo(
        () =>
            nodes?.reduce<string[]>((acc, curr) => {
                const checkIsListNodeFunc = configuration?.isListNode || isSectionNode;
                return curr.id && checkIsListNodeFunc(curr) ? [...acc, curr.id] : acc;
            }, []),
        [configuration?.isListNode, nodes],
    );

    /**
     * Callback that will be executed in order to validate that a section can be moved into another.
     */
    const isMoveSectionSaveDisabled = useCallback(
        (depth, destination) => {
            return isMoveNodeToSectionAllowed({
                depth,
                node: moveNodeDialog.node,
                section: !destination ? { children: nodes, partialPermission: sectionPermission } : destination,
                maxDepth: maxDepthAllowed,
                maxWidth: maxWidthAllowed,
                depthLimitMessage: depthLimitReachedMessage,
                widthLimitMessage: widthLimitReachedMessage,
                editionRestrictionMessage: moveNodeRestrictionMessage,
            });
        },
        [
            depthLimitReachedMessage,
            maxDepthAllowed,
            maxWidthAllowed,
            moveNodeDialog.node,
            moveNodeRestrictionMessage,
            nodes,
            sectionPermission,
            widthLimitReachedMessage,
        ],
    );

    /**
     * Callback to be executed once a section was successfully moved.
     * @param ns - updated nodes
     * @param destination - moved node destination
     */
    const onMovedSection = (ns: NavigationMapItem[], destination?: NavigationMapItem | null) => {
        /**
         * For moving a section to another, we need to update the whole navigation tree. In order to avoid
         * a lot of complexities when moving a section to another and closing all the opened sections and opening
         * new ones, we have decided to just pass the navigation into a loading state, update the data, and display
         * everything once more in the new updated state. To do so, before updating the nodes we pass the navigation
         * into a loading state.
         */
        setIsLoading(true);

        if (onReplaceNodes) {
            /**
             * We then replace the nodes with the new ones, hide the modal window and display the success message on the
             * bottom right corner of the page.
             */
            onReplaceNodes(['children'], ns);

            setMoveNodeDialog({
                isOpen: false,
            });

            const title = translateObject(
                translateFromApiV2Format((moveNodeDialog.node?.title || {}) as TranslatableObject),
            );

            /** Default case "Move" translation */
            let successMessage: TranslateAndReplaceReplacements | TranslateKey = GLOBAL.MOVED;

            if (title) {
                /**
                 * If there is a destination, it means that the element was moved from one section to another. In that case, in order
                 * to display the success message we compute the destination title.
                 *
                 * If destination exists but not destination.title (node unavailable), we keep the default "Moved" key
                 */
                if (destination?.title) {
                    const destinationTitle = translateObject(translateFromApiV2Format(destination.title));

                    successMessage = {
                        key: GLOBAL.ENTITY_MOVED,
                        replacements: {
                            ENTITY: title,
                            TO: destinationTitle || '',
                        },
                    };
                } else if (!destination) {
                    /**
                     * If there is no destination, it means that the element
                     * was moved to the first level. In that case we use a different translation key.
                     */
                    successMessage = {
                        key: movedToFirstLevelKeyMessage,
                        replacements: {
                            ENT: title,
                        },
                    };
                }
            }

            /**
             * We do not want to make the change instantly impact the UI since it would look funky. To avoid so, we use a setTimeout
             * and remove the loading state after half a second.
             */
            setTimeout(() => {
                setIsLoading(false);
                success({ translate: successMessage });
            }, 500);
        }
    };

    /**
     * Callback to be executed when the Move node to section action is triggered
     * @param node - node to be moved
     */
    const onMoveNodeToSection = (node: NavigationMapItem, buttonRef: any) => {
        if (buttonRef) {
            setMoreButtonRef(buttonRef);
        }
        setMoveNodeDialog({
            isOpen: true,
            node,
        });
    };

    /**
     * Callback to be executed when the Edit node to section action is triggered
     * @param node - node to be edited
     */
    const onEditNodeToSection = (path: string[], node: NavigationMapItem, buttonRef: any) => {
        if (buttonRef) {
            setMoreButtonRef(buttonRef);
        }
        setEditNodeDialog({
            isOpen: true,
            node,
            path,
        });
    };

    useEffect(() => {
        if (isChanged) {
            blockNavigation();
        }
        return () => unblockNavigation();
    }, [blockNavigation, isChanged, unblockNavigation]);

    useEffect(() => {
        if (onToggleDialog) {
            onToggleDialog(moveNodeDialog.isOpen || editNodeDialog.isOpen || wizardDialogOpen);
        }
    }, [moveNodeDialog.isOpen, editNodeDialog.isOpen, wizardDialogOpen, onToggleDialog]);

    if (hasError) {
        return <ServiceNotAvailableState onRetry={onReload} />;
    }

    return (
        <>
            <ConnectedBlockedRedirectionDialog />
            <NavigationMapContextProvider
                configuration={configuration}
                defaultFeeds={defaultFeeds}
                visibleByGroupReferences={visibleByGroupReferences}
            >
                {isLoading ? (
                    <FlexBox className="lumx-spacing-margin-huge" orientation="vertical">
                        {/* Fake the filters height in order to avoid layout shifts */}
                        <div style={{ height: '62px' }} />
                        <ExpandableTreeSkeletons />
                    </FlexBox>
                ) : (
                    <>
                        {nodes.length ? (
                            <NavigationList
                                className={`${CLASSNAME} lumx-spacing-padding-huge`}
                                nodes={nodes}
                                id={id}
                                onAddNode={onAddNode}
                                onEditNode={onEditNode ? onEditNodeToSection : undefined}
                                onDeleteNode={onDeleteNode}
                                onReplaceNodes={onReplaceNodes}
                                onMoveNodeToSection={onMoveNodeToSection}
                                currentLanguage={currentLanguage}
                                languages={languages}
                                maxDepthAllowed={maxDepthAllowed}
                                maxWidthAllowed={maxWidthAllowed}
                                depthLimitReachedMessage={depthLimitReachedMessage}
                                widthLimitReachedMessage={widthLimitReachedMessage}
                                scope={scope}
                                wizardOptions={wizardOptions}
                                navigationItemModalProps={navigationItemModalProps}
                                addItemTargetId={addItemTargetId}
                                onToggleWizard={setWizardDialogOpen}
                                rootSectionIds={rootSectionIds}
                                sectionPermission={sectionPermission}
                                // The list of root nodes is not inside a section
                                isInsideSection={false}
                                {...rest}
                            />
                        ) : (
                            <EmptyNavigationList
                                defaultLanguage={currentLanguage}
                                languages={languages}
                                title={emptyTitle}
                                message={emptyMessage}
                                scope={scope}
                                onAdded={
                                    onAddNode
                                        ? (navItem, buttonRef) => onAddNode(['children'], navItem, buttonRef)
                                        : undefined
                                }
                                navigationItemModalProps={navigationItemModalProps}
                                wizardOptions={wizardOptions}
                                isAddNavigationItemOpen={isSameNode(id, addItemTargetId)}
                                onToggleWizard={setWizardDialogOpen}
                            />
                        )}

                        {moveNodeDialog.isOpen && moveNodeDialog.node ? (
                            <SetNodeDestinationDialog
                                isOpen={moveNodeDialog.isOpen}
                                nodeToMove={moveNodeDialog.node}
                                nodes={nodes}
                                onSave={onMovedSection}
                                onClose={() => {
                                    setMoveNodeDialog({ isOpen: false });
                                }}
                                isSaveDisabled={isMoveSectionSaveDisabled}
                                scope={scope}
                                isListNode={configuration.isListNode}
                                defaultLanguage={currentLanguage}
                                languages={languages}
                            />
                        ) : null}

                        {onEditNode && editNodeDialog.isOpen && editNodeDialog.node ? (
                            <EditNodeDialog
                                isOpen={editNodeDialog.isOpen}
                                nodeToEdit={editNodeDialog.node}
                                onEdit={(node) => {
                                    if (editNodeDialog.path && node) {
                                        onEditNode(editNodeDialog.path, node);
                                    }
                                }}
                                onClose={() => {
                                    setEditNodeDialog({ isOpen: false });
                                }}
                                defaultLanguage={currentLanguage}
                                languages={languages}
                                scope={scope}
                                navigationItemModalProps={navigationItemModalProps}
                            />
                        ) : null}
                    </>
                )}
            </NavigationMapContextProvider>
        </>
    );
};

NavigationAdmin.displayName = 'NavigationAdmin';
